/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Divider,
  Spinner,
  Center,
  SimpleGrid,
  Badge,
  Button,
  useToast,
} from "@chakra-ui/react";
import { FaBell, FaEnvelope, FaCommentDots } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";

export default function Notifications() {
  const { i18n } = useTranslation();
  const { isArabic, colors, dir, textAlign } = useTheme();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq").replace(/\/+$/, '');

  // Fetch notifications from API (GET)
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);

        // Use proxy in development, full URL in production


        // Always use tenantId = -1 في لوحة الإدارة لعرض كل الإشعارات
        const tenantId = -1;

        // Fetch notifications using TenantnNotifications API (GET /TenantnNotifications?tenantId=-1)
        const res = await fetch(`${baseUrl}/TenantnNotifications?tenantId=${tenantId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
        });

        // Try to parse response
        let data = [];
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            data = await res.json();
          } catch (parseError) {
            const text = await res.text();
            console.error("Failed to parse JSON response:", parseError);
            console.error("Response text:", text);
            if (text) {
              try {
                data = JSON.parse(text);
              } catch {
                data = [];
              }
            }
          }
        } else {
          const text = await res.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = [];
            }
          }
        }

        if (res.ok && Array.isArray(data) && data.length > 0) {
          // Map API data to notification format
          const mappedNotifications = data.map((notif) => {
            const rating = notif.rating || 0;
            const hasImage = notif.hasImage;
            const rawImageUrl = notif.imageUrl;
            const imageUrl =
              hasImage && rawImageUrl && typeof rawImageUrl === "string"
                ? rawImageUrl
                : "";

            // Use Description / CreateDate from API (capital D / C)
            const content =
              notif.Description ||
              notif.description ||
              "";

            const date =
              notif.CreateDate ||
              notif.createdAt ||
              notif.creationDate ||
              "";

            // العنوان: اسم المتجر (tenantName)
            const displayName = notif.tenantName || "";

            return {
              id: notif.Id || notif.id || notif.notificationId || 0,
              type: rating > 0 ? "review" : "message",
              // العنوان الآن هو اسم المتجر فقط
              title: displayName,
              content,
              date: date || new Date().toLocaleDateString(),
              rating,
              merchantName: notif.merchantName || "",
              tenantName: notif.tenantName || "",
              imageUrl,
              isActive: notif.IsActive,
            };
          });
          setNotifications(mappedNotifications);
          console.log("✅ Notifications loaded from API:", mappedNotifications);
        } else {
          console.warn("No notifications found or API error:", res?.status || "Unknown");
          setNotifications([]);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
        setNotifications([]);

        toast({
          title: isArabic ? "فشل التحميل" : "Load Failed",
          description: isArabic
            ? "فشل تحميل الإشعارات. يرجى المحاولة لاحقاً."
            : "Failed to load notifications. Please try again later.",
          status: "error",
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [baseUrl, isArabic, toast]);
  const updateNotificationStatus = async (notif, isActive) => {
    try {
      const payload = {
        id: notif.id,                 // ⬅️ مهم جداً
        tenantId: notif.tenantId ?? 0,
        tenantName: notif.tenantName,
        merchantName: notif.merchantName,
        hasImage: notif.imageUrl ? 1 : 0,
        imageUrl: notif.imageUrl || "",
        rating: notif.rating || 0,
        description: notif.content,
        createDate: notif.date,
        isActive: isActive,            // ✅ 1 أو 0
      };

      const res = await fetch(`${baseUrl}/TenantnNotifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed");

      // تحديث الواجهة فوراً
      setNotifications(prev =>
        prev.map(n =>
          n.id === notif.id ? { ...n, isActive } : n
        )
      );

      toast({
        title: isActive === 1 ? "تم القبول ✅" : "تم الرفض ❌",
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      toast({
        title: "فشل تحديث الحالة",
        status: "error",
        duration: 4000,
      });
    }
  };


  return (
    <PageContainer title={isArabic ? "الإشعارات والآراء" : "Notifications & Reviews"}>
      {loading ? (
        <Center minH="60vh">
          <Spinner color={colors.mainColor} size="xl" />
        </Center>
      ) : notifications.length === 0 ? (
        <Center minH="60vh">
          <Text color={colors.subText} fontSize="lg">
            {isArabic ? "لا توجد إشعارات حالياً 🔕" : "No notifications currently 🔕"}
          </Text>
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {notifications.map((notif) => (
            <UnifiedCard
              key={notif.id}
              icon={notif.type === "message" ? FaEnvelope : FaCommentDots}
              title={notif.title}
            >
              <HStack mb={3} spacing={3}>
                {notif.isActive === 1 && (
                  <Badge colorScheme="green">مقبول</Badge>
                )}
                {notif.isActive === 0 && (
                  <Badge colorScheme="red">مرفوض</Badge>
                )}
                {(notif.isActive === null || notif.isActive === undefined) && (
                  <Badge colorScheme="yellow">بانتظار المراجعة</Badge>
                )}
              </HStack>

              <HStack spacing={3}>
                <Button
                  size="sm"
                  colorScheme="green"
                  isDisabled={notif.isActive === 1}
                  onClick={() => updateNotificationStatus(notif, 1)}
                >
                  قبول
                </Button>

                <Button
                  size="sm"
                  colorScheme="red"
                  isDisabled={notif.isActive === 0}
                  onClick={() => updateNotificationStatus(notif, 0)}
                >
                  رفض
                </Button>
              </HStack>
              {notif.rating > 0 && (
                <HStack mb={3}>
                  <Text color={colors.gold || "#F2B441"} fontSize="lg">
                    {"★".repeat(notif.rating)}
                    {"☆".repeat(5 - notif.rating)}
                  </Text>
                </HStack>
              )}


              <Text color={colors.subText} mb={3}>
                {notif.content}
              </Text>

              <Divider my={3} borderColor={colors.dividerColor} />

              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text color={colors.subText} fontSize="sm">
                    🕓 {notif.date}
                  </Text>
                  {notif.merchantName && (
                    <Text color={colors.subText} fontSize="xs" opacity={0.8}>
                      {isArabic ? "من: " : "From: "}{notif.merchantName}
                    </Text>
                  )}
                </VStack>
              </HStack>
            </UnifiedCard>
          ))}
        </SimpleGrid>
      )}
    </PageContainer>
  );
}
